/**
 * Auth endpoints.
 *
 * These wrap the endpoints that exist today in `apps/users` — this is the first
 * part of the client that is genuinely integrated rather than reading fixtures.
 */

import { api } from './client.js';
import { session } from '../state/session.js';

/**
 * Sign in with an email address or a phone number.
 *
 * One field for both, because relatives across nine countries will type either
 * and remembering which one they signed up with is a support burden.
 *
 * The response carries the resolved tree context, so the app can render the right
 * family immediately without a second round trip.
 */
export async function login({ identifier, password, treeId }) {
  const data = await api.post('/auth/token/', {
    identifier,
    password,
    ...(treeId ? { tree_id: treeId } : {}),
  });

  session.setTokens({ access: data.access, refresh: data.refresh });
  session.setContext({
    treeId: data.tree_id,
    role: data.role,
    personId: data.person_id,
  });

  // Populates the tree switcher.
  await loadMe();

  return data;
}

/**
 * Create an account.
 *
 * Deliberately does not create a tree. A new user either creates one (becoming an
 * organiser) or accepts an invitation — keeping them apart avoids a graveyard of
 * empty single-person trees.
 */
export async function register({ email, phone, password, displayName, locale, timezoneName }) {
  const data = await api.post('/auth/register/', {
    email: email || null,
    phone: phone || null,
    password,
    display_name: displayName,
    locale: locale ?? 'en',
    timezone_name: timezoneName ?? Intl.DateTimeFormat().resolvedOptions().timeZone,
  });

  session.setTokens({ access: data.access, refresh: data.refresh });
  await loadMe();
  return data;
}

/** Load the signed-in account, including every tree it belongs to. */
export async function loadMe() {
  const user = await api.get('/auth/me/');
  session.setUser(user);
  return user;
}

export async function updateMe(changes) {
  const user = await api.patch('/auth/me/', changes);
  session.setUser(user);
  return user;
}

export function changePassword({ currentPassword, newPassword }) {
  return api.post('/auth/password/change/', {
    current_password: currentPassword,
    new_password: newPassword,
  });
}

/**
 * Request a password reset.
 *
 * Always resolves, whether or not an account matched — the server deliberately
 * gives an identical response either way, so the UI must not imply otherwise.
 * Never render "no account found" here.
 */
export function requestPasswordReset({ identifier }) {
  return api.post('/auth/password/reset/', { identifier });
}

export function confirmPasswordReset({ token, newPassword }) {
  return api.post('/auth/password/reset/confirm/', { token, new_password: newPassword });
}

/**
 * Sign out, here and on the server.
 *
 * Clearing the session locally is the part that must not fail, so it happens
 * regardless: a relative on a train with no signal who presses "Sign out" on a
 * borrowed phone has to end up signed out of that phone. The request to retire the
 * refresh token is attempted first and its failure is swallowed, because the
 * alternative — leaving them signed in because the network was down — is the worse
 * of the two outcomes by a distance.
 *
 * The token is read before `session.clear()` wipes it, which is the ordering bug
 * waiting to happen here.
 */
export function signOut() {
  // Read before clearing — the ordering bug waiting to happen here is wiping the
  // token and then having nothing to send.
  const refresh = session.refreshToken;

  // Cleared synchronously, before the request goes anywhere. Callers navigate away
  // immediately after this and most do not await, so anything that waits on the
  // network first would leave the UI signed in until it came back — and signed in
  // for good if it never did.
  session.clear();

  // Best effort, and deliberately unawaited. `/auth/logout/` is in ANONYMOUS_PATHS,
  // so this carries no Authorization header and cannot trigger the 401-refresh
  // retry with a session that has just been torn down.
  if (refresh) {
    api.post('/auth/logout/', { refresh }).catch(() => {
      // Offline, or the token was already dead. The session has ended locally
      // regardless, which is the part the person pressing the button cares about.
    });
  }
}

// --------------------------------------------------------------------------- //
// Invitations
// --------------------------------------------------------------------------- //

/**
 * Read an invitation before accepting it — unauthenticated.
 *
 * Returns only what the link already implies: the family name, who sent it, their
 * note, and which person on the tree the recipient is being asked to be. A link
 * that reached the wrong hands must not open the family.
 */
export function previewInvitation(token) {
  return api.get(`/auth/invitations/preview/${encodeURIComponent(token)}/`);
}

/**
 * Accept an invitation.
 *
 * Two paths, both handled here:
 *   * a recipient with no account supplies credentials and gets one
 *   * a relative already signed in (because they are on their spouse's tree)
 *     just gains a membership
 *
 * Either way the account attaches to the *existing* Person node. It never
 * creates a second profile for someone the family has already recorded.
 */
export async function acceptInvitation({ token, password, displayName, email, phone }) {
  const data = await api.post('/auth/invitations/accept/', {
    token,
    ...(password ? { password } : {}),
    ...(displayName ? { display_name: displayName } : {}),
    ...(email ? { email } : {}),
    ...(phone ? { phone } : {}),
  });

  session.setTokens({ access: data.access, refresh: data.refresh });
  session.setContext({
    treeId: data.tree_id,
    role: data.role,
    personId: data.person_id,
  });

  await loadMe();
  return data;
}

/** List the active tree's invitations. Administrators only. */
export function listInvitations({ cursor } = {}) {
  return api.list('/auth/invitations/', { query: { cursor } });
}

/**
 * Invite one relative to claim one Person node.
 *
 * `personId` is required: an invitation connects someone to the record their
 * family already built for them.
 *
 * The response includes `accept_url` so an admin who chose the "copied link"
 * channel can paste it into WhatsApp themselves. It is returned once and never
 * stored — the server keeps only a hash of the token.
 */
export function sendInvitation({
  personId,
  email,
  phone,
  channel = 'email',
  message = '',
  role = 'member',
  tellMeWhenOpened = true,
  remindAutomatically = true,
  alsoOnTrees = [],
  showBeforeJoining = 'tree',
}) {
  return api.post('/auth/invitations/', {
    person_id: personId,
    // Omitted rather than nulled when absent: the server falls back to the address on their
    // record, and a null would look like an answer.
    ...(email ? { email } : {}),
    ...(phone ? { phone } : {}),
    channel,
    message,
    role,
    tell_me_when_opened: tellMeWhenOpened,
    remind_automatically: remindAutomatically,
    also_on_trees: alsoOnTrees,
    show_before_joining: showBeforeJoining,
  });
}

/**
 * Send an existing invitation again.
 *
 * Issues a fresh link, because only a hash of the original is stored and a reminder
 * that cannot carry a working link is an email telling somebody to click nothing. The
 * earlier link stops working — the copy on the node says so before the press.
 *
 * Capped at three and spaced by days on the server. The refusal comes back as a
 * sentence to show, not a code to translate.
 */
export function remindInvitation(invitationId) {
  return api.post(`/auth/invitations/${invitationId}/remind/`, {});
}

export function revokeInvitation(invitationId) {
  return api.post(`/auth/invitations/${invitationId}/revoke/`, {});
}
